/**
 * C.3a — SharePoint folder import.
 *
 * Reuses lib/graph utilities to walk a SharePoint folder, pull each
 * markdown / flow / asset file, and yield ImportFile entries. Full
 * Graph integration is wired in C.5+ (when ObsidianVaultWindow needs to
 * read the same surface for live mode); the wizard surface here just
 * declares the path and the helper signature it expects.
 *
 * Per memory feedback_graph_retry_policy.md: every Graph call inside
 * the iterator wraps in withRetry(READ).
 */

import type { ImportFile, ImportSource } from './types';
import { isTextExt } from './walk';

export interface SharePointImportTarget {
  siteId: string;
  driveId: string;
  /** Drive item id of the folder to walk; omit for drive root. */
  folderItemId?: string;
  /** Display label for the wizard. */
  label: string;
}

export interface SharePointDriveItemLike {
  id: string;
  name: string;
  size?: number;
  eTag?: string;
  lastModifiedDateTime?: string;
  file?: { mimeType?: string };
  folder?: { childCount?: number };
}

/**
 * Caller-supplied Graph adapter. Kept as a parameter so this module
 * stays free of any specific graphClient import (C.3 surface lands
 * before the real adapter ships in C.5/C.9). Tests can swap in fakes.
 */
export interface SharePointReader {
  listChildren(
    driveId: string,
    itemId: string | 'root',
  ): Promise<SharePointDriveItemLike[]>;
  getTextContent(driveId: string, itemId: string): Promise<string>;
  getBytesContent(driveId: string, itemId: string): Promise<Uint8Array>;
}

async function* walkSharePoint(
  reader: SharePointReader,
  target: SharePointImportTarget,
  rootItemId: string | 'root',
  prefix: string,
): AsyncIterableIterator<ImportFile> {
  const children = await reader.listChildren(target.driveId, rootItemId);
  for (const child of children) {
    const path = prefix ? `${prefix}/${child.name}` : child.name;
    if (child.folder) {
      yield* walkSharePoint(reader, target, child.id, path);
    } else {
      const dot = child.name.lastIndexOf('.');
      const ext = dot > 0 ? child.name.slice(dot + 1).toLowerCase() : '';
      const isText = isTextExt(ext);
      const out: ImportFile = {
        path,
        ext,
        sizeBytes: child.size ?? 0,
        lastModifiedMs: child.lastModifiedDateTime
          ? Date.parse(child.lastModifiedDateTime)
          : undefined,
        etag: child.eTag,
        externalId: child.id,
        mimeType: child.file?.mimeType,
      };
      if (isText) out.text = await reader.getTextContent(target.driveId, child.id);
      else out.bytes = await reader.getBytesContent(target.driveId, child.id);
      yield out;
    }
  }
}

export function buildSharePointSource(
  reader: SharePointReader,
  target: SharePointImportTarget,
): ImportSource {
  return {
    kind: 'sharepoint',
    label: target.label,
    files: () => walkSharePoint(reader, target, target.folderItemId ?? 'root', ''),
  };
}
